import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getCategories } from '../firebase/firestore';
import { subscribeToActiveAds } from '../firebase/firestore';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import './Navbar.css';

const DEFAULT_CATEGORIES = ['Politics', 'Sports', 'Technology', 'Entertainment', 'Business', 'World', 'Health', 'Education'];
const AD_ROTATE_INTERVAL = 4000;

export default function Navbar() {
  const [cats, setCats] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [headerAds, setHeaderAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [adFading, setAdFading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dropRef = useRef();
  const rotateTimerRef = useRef(null);

  useEffect(() => {
    getCategories().then(c => setCats(c.length ? c : DEFAULT_CATEGORIES.map(n => ({ name: n, slug: n.toLowerCase() }))));
    const unsub = subscribeToActiveAds('header', ads => {
      setHeaderAds(ads || []);
      setCurrentAdIndex(0);
    });
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);

    // Live clock
    const clockTimer = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      unsub();
      window.removeEventListener('scroll', onScroll);
      clearInterval(clockTimer);
    };
  }, []);

  useEffect(() => {
    if (headerAds.length <= 1) return;
    rotateTimerRef.current = setInterval(() => {
      setAdFading(true);
      setTimeout(() => {
        setCurrentAdIndex(prev => (prev + 1) % headerAds.length);
        setAdFading(false);
      }, 400);
    }, AD_ROTATE_INTERVAL);
    return () => clearInterval(rotateTimerRef.current);
  }, [headerAds]);

  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setCatOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchVal.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchVal.trim())}`);
      setSearchVal('');
      setMenuOpen(false);
    }
  };

  const currentAd = headerAds[currentAdIndex] || null;

  const timeStr = currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = currentTime.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <>
      {/* Top info bar */}
      <div className="topbar">
        <div className="container topbar-inner">
          <span className="topbar-date">📅 {dateStr}</span>
          <span className="topbar-clock">🕐 {timeStr}</span>
          {/* <span className="topbar-edition">NewsBuzz Today — Multilingual Edition</span> */}
        </div>
      </div>

      <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-inner container">
          <Link to="/" className="navbar-brand">
            <img
              src="/newsbuzztoday_logo.svg"
              alt="NewsBuzz Today"
              className="brand-logo"
            />
            <div className="brand-text">
              <span className="brand-name">NewsBuzz Today</span>
              <span className="brand-tagline">Breaking News, Every Moment.</span>
            </div>
          </Link>

          {currentAd && (
            <div className="header-ad-inline">
              <span className="header-ad-badge">Ad</span>
              <a
                href={currentAd.linkURL}
                target="_blank"
                rel="noopener noreferrer"
                className={`header-ad-link ${adFading ? 'fading' : ''}`}
              >
                <img src={currentAd.imageURL} alt={currentAd.title} />
                {currentAd.title && <span className="header-ad-title">{currentAd.title}</span>}
              </a>
              {headerAds.length > 1 && (
                <div className="header-ad-dots">
                  {headerAds.map((_, i) => (
                    <button key={i} className={`ad-dot ${i === currentAdIndex ? 'active' : ''}`}
                      onClick={() => setCurrentAdIndex(i)} aria-label={`Ad ${i + 1}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          <nav className={`navbar-links ${menuOpen ? 'open' : ''}`}>
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>Home</Link>

            <div className="nav-dropdown" ref={dropRef}>
              <button className="nav-link dropdown-trigger" onClick={() => setCatOpen(!catOpen)}>
                Categories <span className="arrow">{catOpen ? '▲' : '▼'}</span>
              </button>
              {catOpen && (
                <div className="dropdown-menu">
                  {cats.map(c => (
                    <Link key={c.name} to={`/category/${c.slug || c.name.toLowerCase()}`}
                      className="dropdown-item" onClick={() => { setCatOpen(false); setMenuOpen(false); }}>
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/epaper" className={`nav-link ${location.pathname === '/epaper' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>📄 E-Paper</Link>
            <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>About</Link>

            <form onSubmit={handleSearch} className="nav-search">
              <input value={searchVal} onChange={e => setSearchVal(e.target.value)} placeholder="Search news..." />
              <button type="submit">🔍</button>
            </form>
          </nav>

          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Language Selection Bar */}
      <div className="language-bar">
        <div className="container language-bar-inner">
          <span className="lang-label">🌐 Edition:</span>
          <div className="lang-tabs">
            <button
              className={`lang-tab ${language === 'all' ? 'active' : ''}`}
              onClick={() => setLanguage('all')}
            >
              🌍 All Languages
            </button>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                className={`lang-tab lang-tab-${lang.code} ${language === lang.code ? 'active' : ''}`}
                onClick={() => setLanguage(lang.code)}
              >
                {lang.flag} {lang.label}
                <span className="lang-native">{lang.nativeLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}